/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { Button, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { NumberInputField } from 'components/Input'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ConfirmationModal } from 'components/Modals/ConfirmationModal/ConfirmationModal'
import { BreakSectionFormItem, validateBreakDuration, validateBreakQuestionIndex } from 'pagesNew/PracticeTests/utils'
import * as classes from './styles'

export type BreakSectionsFieldArrayProps = {
  fields: FieldArrayRenderProps<BreakSectionFormItem, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<BreakSectionFormItem, HTMLElement>['meta']
  questionsCount: number
}

export const BreakSectionsFieldArray = ({ fields, meta, questionsCount }: BreakSectionsFieldArrayProps) => {
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
  const canAddBreak = questionsCount >= 2

  const handleAdd = () => {
    fields.push({ questionIndex: '', duration: '' })
  }

  const handleConfirm = () => {
    if (indexToRemove !== null) {
      setIndexToRemove(null)
      fields.remove(indexToRemove)
    }
  }

  return (
    <div>
      <Typography variant="body2" css={classes.label}>
        Break Sections
      </Typography>
      <div>
        {fields.map((field, index) => {
          const hasError = meta.error?.[index] && meta.touched

          return (
            <div key={field} css={[classes.row, hasError && classes.rowError]}>
              <div css={classes.textWrapper}>
                <Typography variant="body2" css={classes.label}>
                  After question #
                </Typography>
                <div css={classes.field}>
                  <NumberInputField
                    name={`${field}.questionIndex`}
                    validate={validateBreakQuestionIndex}
                    error={meta.error?.[index]?.questionIndex}
                    fullWidth
                    data-testid={`break-section-${index}-question-index`}
                  />
                </div>
                <Typography variant="body2" css={classes.label}>
                  show break for
                </Typography>
                <div css={classes.field}>
                  <NumberInputField
                    name={`${field}.duration`}
                    validate={validateBreakDuration}
                    error={meta.error?.[index]?.duration}
                    fullWidth
                    data-testid={`break-section-${index}-duration`}
                  />
                </div>
                <Typography variant="body2" css={classes.label}>
                  minutes
                </Typography>
              </div>
              <Button
                variant="outlined"
                color="red"
                size="small"
                onClick={() => setIndexToRemove(index)}
                css={classes.iconButton}
                data-testid={`remove-break-section-${index}`}
              >
                <TrashIcon />
              </Button>
            </div>
          )
        })}
      </div>
      <Button
        variant="outlined"
        color="blue"
        size="large"
        onClick={handleAdd}
        disabled={!canAddBreak}
        css={classes.addContentButton}
      >
        Add Break <AddRoundedIcon />
      </Button>
      <ConfirmationModal
        open={indexToRemove !== null}
        title={`Deleting Break Section #${(indexToRemove ?? 0) + 1}`}
        subtitle="Are you sure you want to delete this break section?"
        onClose={() => setIndexToRemove(null)}
        onConfirm={handleConfirm}
        confirmText="Delete"
        closeText="Cancel"
      />
    </div>
  )
}
